import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
  DEFAULT_THEME_MODE,
  THEME_MODE_STORAGE_KEY,
  THEME_PRIMARY_VAR,
  THEME_SECONDARY_VAR,
  THEME_STORAGE_KEY,
  ThemeMode
} from "config/constants/theme";


type ThemeColors = {
  primary?: string;
  secondary?: string;
};

type ThemeColorsByMode = {
  dark?: ThemeColors;
  light?: ThemeColors;
};

const parseColorToRgbComponents = (color: string): string | null => {
  if (!color) return null;

  const trimmed = color.trim().toLowerCase();

  // HEX formats: #rgb or #rrggbb
  if (trimmed.startsWith("#")) {
    let hex = trimmed.slice(1);

    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((ch) => ch + ch)
        .join("");
    }

    if (hex.length !== 6) return null;

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);

    if ([r, g, b].some((v) => Number.isNaN(v))) return null;

    return `${r} ${g} ${b}`;
  }

  // rgb() or rgba()
  if (trimmed.startsWith("rgb")) {
    const match = trimmed.match(/rgba?\(([^)]+)\)/);
    if (!match) return null;

    const parts = match[1]
      .split(",")
      .map((p) => p.trim())
      .slice(0, 3);

    if (parts.length !== 3) return null;

    const [r, g, b] = parts.map((p) => parseInt(p, 10));
    if ([r, g, b].some((v) => Number.isNaN(v))) return null;

    return `${r} ${g} ${b}`;
  }

  return null;
};

const parseStoredColors = (rawValue: string | null): ThemeColorsByMode | null => {
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue);

    if (typeof parsed === "object" && parsed !== null) {
      // New format: { dark: { primary, secondary }, light: { primary, secondary } }
      if ("dark" in parsed || "light" in parsed) {
        const byMode: ThemeColorsByMode = {};

        if (parsed.dark && typeof parsed.dark === "object") {
          byMode.dark = {
            primary: typeof parsed.dark.primary === "string" ? parsed.dark.primary : undefined,
            secondary: typeof parsed.dark.secondary === "string" ? parsed.dark.secondary : undefined,
          };
        }

        if (parsed.light && typeof parsed.light === "object") {
          byMode.light = {
            primary: typeof parsed.light.primary === "string" ? parsed.light.primary : undefined,
            secondary: typeof parsed.light.secondary === "string" ? parsed.light.secondary : undefined,
          };
        }

        return byMode;
      }

      // Old format: flat { primary, secondary } – apply to both modes
      const flat: ThemeColors = {
        primary: typeof (parsed as any).primary === "string" ? (parsed as any).primary : undefined,
        secondary: typeof (parsed as any).secondary === "string" ? (parsed as any).secondary : undefined,
      };

      if (flat.primary || flat.secondary) {
        return { dark: flat, light: flat };
      }
    }
  } catch {
    // ignore
  }

  return null;
};

export const getStoredThemeColors = (mode?: ThemeMode): ThemeColors | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const rawValue = window.localStorage.getItem(THEME_STORAGE_KEY);
  const byMode = parseStoredColors(rawValue);
  if (!byMode) {
    return null;
  }

  const effectiveMode: ThemeMode = mode || getStoredThemeMode();
  return byMode[effectiveMode] || null;
};

export const applyThemeColorsToDocument = (colors: ThemeColors) => {
  if (typeof document === "undefined") {
    return;
  }

  const { primary, secondary } = colors;
  const resolvedPrimary = primary || DEFAULT_PRIMARY_COLOR;
  const resolvedSecondary = secondary || DEFAULT_SECONDARY_COLOR;

  document.documentElement.style.setProperty(
    THEME_PRIMARY_VAR,
    resolvedPrimary
  );
  document.documentElement.style.setProperty(
    THEME_SECONDARY_VAR,
    resolvedSecondary
  );

  const primaryRgb = parseColorToRgbComponents(resolvedPrimary);
  if (primaryRgb) {
    document.documentElement.style.setProperty(`${THEME_PRIMARY_VAR}-rgb`, primaryRgb);
  }

  const secondaryRgb = parseColorToRgbComponents(resolvedSecondary);
  if (secondaryRgb) {
    document.documentElement.style.setProperty(`${THEME_SECONDARY_VAR}-rgb`, secondaryRgb);
  }
};

export const bootstrapStoredThemeColors = () => {
  const storedColors = getStoredThemeColors();

  if (!storedColors) {
    return;
  }

  applyThemeColorsToDocument(storedColors);
};

export const getStoredThemeMode = (): ThemeMode => {
  if (typeof window === "undefined") {
    return DEFAULT_THEME_MODE;
  }

  const storedMode = window.localStorage.getItem(THEME_MODE_STORAGE_KEY) as ThemeMode | null;

  if (storedMode === "dark" || storedMode === "light") {
    return storedMode;
  }

  return DEFAULT_THEME_MODE;
};

export const applyThemeModeToDocument = (mode: ThemeMode) => {
  if (typeof document === "undefined") {
    return;
  }

  document.documentElement.dataset.theme = mode;
  document.documentElement.style.setProperty("color-scheme", mode);
};

export const persistThemeMode = (mode: ThemeMode) => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(THEME_MODE_STORAGE_KEY, mode);
};

export const bootstrapStoredThemeMode = () => {
  const mode = getStoredThemeMode();
  applyThemeModeToDocument(mode);
};

